import {BrandInvoice, InvoiceState} from "../../../schema";
import React from "react";
import Countries from "../../../models/utils/Countries";
import Money from "../../../utils/Money";
import {printInvoiceState} from "./InvoiceListScene";
import OldUrls from "../../../models/utils/OldUrls";
import FormField from "../../../ui/form/FormField";
import UIInput from "../../../ui/form/UIInput";
import UISelect from "../../../ui/form/UISelect";
import {Form as FormikForm, Formik, FormikHelpers, FormikValues} from "formik";
import * as Yup from "yup";
import PrimaryButton from "../../../ui/PrimaryButton";
import TextButton from "../../../ui/TextButton";
import {useMutation} from "@apollo/client";
import gql from "graphql-tag";
import {handleError, handleSuccess} from "../../../utils/Apollo";

type InvoicePageProps = {
  invoice: BrandInvoice
  onClose: () => void
}

export default function InvoicePage({invoice, onClose}: InvoicePageProps) {
  const [submit] = useMutation(mutation);

  const initialValues = {
    remoteId: invoice.remoteId || '',
    state: invoice.state,
    sentDate: invoice.sentDate,
  }

  const validation = Yup.object().shape({});

  const onFormSubmit = (values: FormikValues, helpersReact: FormikHelpers<FormikValues>) => {
    helpersReact.setSubmitting(true);
    submit({
      variables: {id: invoice.id, data: values}
    }).then(
      (result) => {
        onClose();
        handleSuccess('Invoice updated')
      },
      (apolloError) => handleError(apolloError)
    ).finally(() => helpersReact.setSubmitting(false));
  };

  const states = [
    {value: InvoiceState.cancelled, label: 'Cancelled'},
    {value: InvoiceState.draft, label: 'Draft'},
    {value: InvoiceState.final, label: 'Final'},
    {value: InvoiceState.paid, label: 'Paid'},
    {value: InvoiceState.review, label: 'Review'},
  ]

  return (
    <div>
      {printInvoiceState(invoice.id, invoice.state)}
      <div className="flex flex-col gap-8 mt-4 text-sm">
        <div className="grid grid-cols-2 gap-x-8">
          <div>
            <address>
              <strong>{invoice.organisation}</strong><br/>
              {invoice.address?.street1}{invoice.address?.street2 ? `, ${invoice.address.street2}` : ''}<br/>
              {invoice.address?.city}{invoice.address?.province ? `, ${invoice.address.province}` : ''}<br/>
              {Countries.getName(invoice.address?.country || '')}{invoice.address?.postcode ? `, ${invoice.address.postcode}` : ''}
            </address>
            {invoice.taxId &&
                <div className="mt-2">
                    <strong>Tax Id:</strong> {invoice.taxId}
                </div>
            }
          </div>
          <div className="break-words">
            <strong>Send to:</strong><br/>
            {invoice.to.name} <br/>&lt;{invoice.to.email}&gt;
          </div>
        </div>
        <table className="min-w-full divide-y divide-gray-dark">
          <thead className="bg-gray-light text-gray-dark">
          <tr>
            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold sm:pl-6">
              Description
            </th>
            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">
              Amount
            </th>
          </tr>
          </thead>
          <tbody className="divide-y divide-gray-dark bg-white">
          <tr>
            <td className="py-4 pl-4 pr-3 text-sm font-mediumsm:pl-6">
              {invoice.description}<br/>
              <a href={OldUrls.event(invoice.eventId)} className="text-blue">See event</a>
            </td>
            <td className="whitespace-nowrap px-3 py-4 text-sm">{new Money(invoice.amount).format()}</td>
          </tr>
          </tbody>
        </table>
        <div>
          <Formik initialValues={initialValues} validationSchema={validation} onSubmit={onFormSubmit}>
            {({isSubmitting}) => (
              <FormikForm className="shadow-lg p-4 py-6">
                <FormField id={'remoteId'} label={'Invoice # in invoicing system'}>
                  <UIInput name="remoteId" id="remoteId" className="w-full"/>
                </FormField>
                <FormField id="state" label="State">
                  <UISelect name="state" options={states} id="state" className="w-full"/>
                </FormField>
                <FormField id="sentDate" label="Invoice sent on">
                  <UIInput name="sentDate" id="sentDate" type="date" className="w-full"/>
                </FormField>
                <div className="flex gap-x-4 justify-end">
                  <PrimaryButton loading={isSubmitting}> Save</PrimaryButton>
                  <TextButton onClick={() => onClose()}>Cancel</TextButton>
                </div>
              </FormikForm>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}

const mutation = gql`
    mutation UpdateInvoiceMutation($id: ID!, $data: BrandInvoiceInput!) {
        updateInvoice(id: $id, data: $data) {
            id
            state
            remoteId
            sentDate
        }
    }
`;
